import { useEffect, useState, type ReactNode } from 'react'
import { listIdsInFilter, moveSelectionToTrash, type BulkTrashKind, type BulkTrashResult } from '../api'
import { useToast } from '../layout/ToastContext'
import type { Column } from './DataTable'
import ConfirmDialog from './ConfirmDialog'
import { fill, COMPONENTS_BULK_TRASH_TEXTS as TX } from '../../shared/texts'

// Mehrfachauswahl → Papierkorb, shared by the customer, abo, user and author lists (operator,
// 17.10.2026: „bei allen Listen … eine Checkbox für Bulk“). ONE place for the behaviour and the
// wording: a selection column, „alle auf dieser Seite“ in its header, „alle N im Filter“ once the
// page is fully ticked, a confirmation with the count and the result with what was skipped.
//
// The rules live on the server (POST /<kind>/trash runs the single delete's checks per record);
// here the column only exists with the delete right, and `whyNot` greys out what the list already
// knows cannot go (the own user account). Whatever the server still refuses comes back by name.
//
// The selection is a set of ids, kept across pages, cleared when the search or a filter changes —
// „alle N im Filter“ means the filter it was taken under.

interface Options<T> {
    kind: BulkTrashKind
    enabled: boolean                 // the delete right of this list
    rows: T[]                        // the page on screen
    rowId: (row: T) => string
    total: number                    // rows in the current search/filter
    filter: Record<string, string | undefined> // q + the list's filters, as the list sends them
    whyNot?: (row: T) => string      // non-empty → this row cannot be selected, shown as tooltip
    onDone: () => void               // after moving: reload (publishChange)
}

export interface BulkTrash<T> {
    column: Column<T> | null         // prepend to the list's columns
    bar: ReactNode                   // above the table, only while something is selected
    dialogs: ReactNode
}

const plural = (n: number) => (n === 1 ? TX.entryOne : fill(TX.entryMany, { n }))

export function useBulkTrash<T>({ kind, enabled, rows, rowId, total, filter, whyNot, onDone }: Options<T>): BulkTrash<T> {
    const { toast } = useToast()
    const [selected, setSelected] = useState<Set<string>>(new Set())
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')
    const [result, setResult] = useState<BulkTrashResult | null>(null)

    const filterKey = JSON.stringify(filter)
    useEffect(() => { setSelected(new Set()) }, [filterKey])

    const selectable = rows.filter((r) => !whyNot?.(r))
    const pageAll = selectable.length > 0 && selectable.every((r) => selected.has(rowId(r)))

    const toggle = (id: string) => setSelected((prev) => {
        const next = new Set(prev)
        if (next.has(id)) next.delete(id); else next.add(id)
        return next
    })
    const togglePage = () => setSelected((prev) => {
        const next = new Set(prev)
        for (const r of selectable) { if (pageAll) next.delete(rowId(r)); else next.add(rowId(r)) }
        return next
    })

    async function selectAllInFilter() {
        try {
            const r = await listIdsInFilter(kind, filter)
            // Rows the list greys out stay out here too — they are on the page we can see.
            const blocked = new Set(rows.filter((row) => whyNot?.(row)).map(rowId))
            setSelected(new Set(r.ids.filter((id) => !blocked.has(id))))
            if (r.truncated) toast(fill(TX.truncated, { n: r.ids.length }), 'err')
        } catch (e: any) {
            toast(e?.response?.data?.error ?? TX.selectFailed, 'err')
        }
    }

    async function run() {
        setBusy(true); setError('')
        try {
            const r = await moveSelectionToTrash(kind, [...selected])
            setConfirmOpen(false)
            setSelected(new Set())
            onDone()
            if (r.skipped.length) setResult(r)
            else toast(fill(TX.moved, { moved: r.moved, skipped: 0 }))
        } catch (e: any) {
            setError(e?.response?.data?.error ?? TX.moveFailed)
        } finally {
            setBusy(false)
        }
    }

    if (!enabled) return { column: null, bar: null, dialogs: null }

    const column: Column<T> = {
        key: 'select', className: 'center select-col', defaultWidth: 32,
        header: <input type="checkbox" className="check" aria-label={TX.selectPage}
            title={TX.selectPage} checked={pageAll} disabled={selectable.length === 0}
            onChange={togglePage} />,
        render: (row) => {
            const id = rowId(row)
            const why = selected.has(id) ? '' : (whyNot?.(row) ?? '')
            return <input type="checkbox" className="check" aria-label={TX.select} checked={selected.has(id)}
                disabled={!!why} title={why || TX.select} onChange={() => toggle(id)} />
        },
    }

    const bar = selected.size > 0 ? (
        <div className="row bulk-bar">
            <span>{fill(TX.selectedCount, { entries: plural(selected.size) })}</span>
            {pageAll && total > selected.size && (
                <button type="button" className="secondary" onClick={selectAllInFilter}>{fill(TX.selectAllInFilter, { total })}</button>
            )}
            <button type="button" className="secondary" onClick={() => setSelected(new Set())}>{TX.clearSelection}</button>
            <button type="button" className="danger" onClick={() => { setError(''); setConfirmOpen(true) }}>
                {fill(TX.toTrashCount, { n: selected.size })}
            </button>
        </div>
    ) : null

    const dialogs = <>
        <ConfirmDialog
            open={confirmOpen}
            title={TX.toTrash}
            message={<>{fill(TX.moveQuestion, { entries: plural(selected.size) })}<br />{TX.skipNote}</>}
            confirmLabel={TX.toTrash}
            danger
            busy={busy}
            error={error}
            onConfirm={run}
            onCancel={() => { if (!busy) setConfirmOpen(false) }}
        />
        <ConfirmDialog
            open={!!result}
            title={TX.result}
            message={result ? <>
                {fill(TX.moved, { moved: result.moved, skipped: result.skipped.length })}
                {/* Spans, not a list: the dialog puts the message into a <p>. */}
                <span className="bulk-skipped">
                    {result.skipped.map((s) => <span key={s.id}><strong>{s.label}</strong>: {s.reason}</span>)}
                </span>
            </> : null}
            onConfirm={() => setResult(null)}
        />
    </>

    return { column, bar, dialogs }
}
